import React, { Component, Suspense } from 'react'

import { compose } from 'redux'
import { Switch, Route, withRouter } from 'react-router-dom'
import ShiftsWidget from './Widgets/ShiftsWidget'
import ApiErrorCatcher from './Components/shared/ApiErrorCatcher'
import AppCrash from './Components/shared/AppCrash'
import AppBanner from './Components/shared/AppBanner'
import AppLoading from './Components/shared/AppLoading'
import AppManager from './Views/AppManager'
import Cookie from 'js-cookie'
import LoginView from './Views/shared/LoginView'
import OptinsForm from './Components/anonymous/OptinsForm'
import PasswordResetAskForm from './Components/anonymous/PasswordResetAskForm'
import PasswordResetForm from './Components/anonymous/PasswordResetForm'
import AnonymousFormWrapper from './Components/shared/AnonymousFormWrapper'
import NonProdEnvironmentBanner from './Components/shared/NonProdEnvironmentBanner'
import ConfirmAdditionalEmail from './Components/anonymous/ConfirmAdditionalEmail'
import Theme from './Components/shared/Theme'
import UserSwitch from './Components/anonymous/UserSwitch'
import ChatManager from './Components/shared/ChatManager'
import blue from './themes/blue.json'
import { mapStateToProps, mapDispatchToProps, connect } from './reducers/Dispatchers'
import {
  request,
  getUrlParameterByName,
  getCurrentUrl,
  objectDeepCopy,
  getContextFromRoles,
  filterSectionsAndSetRootSectionOfSchool
} from './utils'
import { Contexts, SchoolRoles, OtherRoles, InstitutionRoles, SUPPORTED_LANGUAGE_LOCALES } from './utils/constants'
import { HttpMethods } from './utils/apiHelper'
import appConfig from './config'
import { setLanguage } from './utils/locale'
import Analytics from './utils/analytics'
import { Routes } from './routes'
import ChangeLogNotification from './Views/shared/ChangeLogNotification'
import GlobalProvider from './Providers/GlobalProvider'
import { fetchSchoolOptions } from './utils/api/school'
import { refresh, switchProfile } from './utils/api/auth'
import { normalizeCurrentUser } from './utils/api/user'
import GlobalFiltersProvider from './Providers/GlobalFiltersProvider'
import SchoolProvider from './Providers/SchoolProvider'
import AuthCallback from './Views/shared/AuthCallback'
import AuthProvider, { StorageKeys } from './Providers/AuthProvider'
import { HttpStatuses } from './utils/http'
import { Cookies } from './utils/Cookie'
import { getPasswordResetLink } from './utils/passwordResetLink'

import '../src/utils/FontAwesomeLibrary'
import './assets/index.scss'
import 'antd/dist/antd.css'

// use React.lazy to load the student validation component and to have code splitting
const SectionEvaluationByStudent = React.lazy(() => import('./Components/anonymous/SectionEvaluationByStudent'))

class App extends Component {
  constructor (props) {
    super(props)

    this.state = {
      appCrashedInfo: null,
      showOptinsForm: false,
      /**
       * optinsForUser -> if this is true we show only one agreement checkbox, the "recruitment" option is used only for students
       */
      optinsForUser: false,
      ready: false,
      languageSet: false,
      isWidgetRoute: window.location.href.includes('/widget/'),
      schoolOptions: []
    }

    // initialize analytics
    Analytics.initialize()

    this.previouslySelectedLanguage = null
  }

  /** The blocking terms-and-conditions acceptance screen is displayed. */
  get optinsDisplayed () {
    return this.state.showOptinsForm
  }

  componentDidMount () {
    if (window.location.href.includes('/widget/')) {
      // don't do any of the automatic loading if we are running in "widget" mode
      return
    }
    // Links sent by email can carry the language of their recipient so that the page shows up in
    // that language before any login: `&user-language=fr_be` for the account creation emails (staff
    // and students alike), `&language=fr_be` for the other links.
    // This only selects the language: the translations are loaded right after by checkUserTimer, and
    // the profile language takes over as soon as a user is loaded.
    const urlLanguage = (getUrlParameterByName('user-language') ?? getUrlParameterByName('language'))?.toLowerCase()
    if (SUPPORTED_LANGUAGE_LOCALES.includes(urlLanguage)) {
      this.props.setActiveLanguage(urlLanguage)
    }

    this.props.setTheme(blue)
    this.checkUserTimerIndex = 0
    this.checkUserTimer = setInterval(async () => {
      if (this.props.getUser.id || this.checkUserTimerIndex === 10) {
        await this.executeSetLanguage(this.props.getActiveLanguage)
        this.setState({ languageSet: true })
        clearInterval(this.checkUserTimer)
      } else {
        this.checkUserTimerIndex += 1
      }
    }, 100)
    this.refreshAppBannerWarning()
    this.timerRefreshAppBannerWarning = setInterval(
      () => this.refreshAppBannerWarning(),
      60000
    )
    // Sliding JWT renewal (24 h backend TTL): as long as the app stays open, the token is reissued
    // every hour so that an active session does not die after 24 h. Does nothing while no user is
    // logged in (guard in refreshSession).
    this.refreshTokenTimer = setInterval(() => this.refreshSession(), 3600000)

    // check if we have evaluation token. This means a student want's to give the evaluation for the institution
    const evaluationToken = getUrlParameterByName('section-evaluation-token')
    if (
      typeof evaluationToken !== 'undefined' &&
      evaluationToken !== null &&
      evaluationToken !== ''
    ) {
      Cookie.remove(Cookies.TOKEN)
      this.setState({
        ready: true
      })
      // pass the search query too
      this.props.history.push({ pathname: Routes.SECTION_EVALUATION_BY_STUDENT, search: window.location.search })
      return
    }

    // Password-choice links received by email: "forgotten password"
    // ({hostname}/?password-reset-token={token}) and activation of a brand new account
    // ({hostname}/?user-token={token} for staff, ?student-token={token} for students).
    // Same token, same page: see getPasswordResetLink.
    if (getPasswordResetLink() !== null) {
      Cookie.remove(Cookies.TOKEN)
      this.setState({
        ready: true
      })
      this.props.history.push({ pathname: Routes.PASSWORD_RESET, search: window.location.search })
      return
    }

    const emailValidationToken = getUrlParameterByName('email-validation-token')
    if (
      typeof emailValidationToken !== 'undefined' &&
      emailValidationToken !== null &&
      emailValidationToken !== ''
    ) {
      Cookie.remove(Cookies.TOKEN)
      this.setState({
        ready: true
      })
      this.props.history.push({ pathname: Routes.CONFIRM_ADDITIONAL_EMAIL, search: window.location.search })
      return
    }

    if (
      typeof Cookie.get(Cookies.TOKEN) !== 'undefined' &&
      Cookie.get(Cookies.TOKEN) !== null &&
      Cookie.get(Cookies.TOKEN) !== ''
    ) {
      const switchUserId = Cookie.get(Cookies.SWITCH_USER)
      if (
        typeof switchUserId !== 'undefined' &&
        switchUserId !== null &&
        switchUserId !== ''
      ) {
        this.getUserContextAndData(
          JSON.parse(Cookie.get(Cookies.TOKEN)),
          undefined,
          true,
          true
        ).then(() => {
          this.autoLoadUser()
        }).catch(() => this.handleBootstrapFailure())
      } else {
        this.getUserContextAndData(JSON.parse(Cookie.get(Cookies.TOKEN)))
          .catch(() => this.handleBootstrapFailure())
      }
      return
    } else {
      if (![Routes.PASSWORD_RESET, Routes.AUTH_CALLBACK].includes(this.props.location.pathname)) {
        // redirect to login
        this.navigateToLoginPage()
      }
    }

    this.setState({ ready: true })
  }

  componentWillUnmount () {
    clearInterval(this.timerRefreshAppBannerWarning)
    // Historical bug: the counter (checkUserTimerIndex) was cleared instead of the interval id.
    clearInterval(this.checkUserTimer)
    clearInterval(this.refreshTokenTimer)
  }

  /**
   * Reissues the JWT of the current profile as long as a session is active (see refresh()). On
   * success, updates the in-memory token AND, when a "remember me" cookie exists, persists it again
   * (new 7-day window). A failure (token already expired, network) is ignored: the next real request
   * will trigger the 401 → logout.
   */
  refreshSession = async () => {
    const currentUser = this.props.getUser

    // No session, admin impersonation (the admin's own token, not renewable this way), or app
    // blocked on the optins screen: nothing to renew.
    if (!currentUser || !currentUser.token || currentUser.switchUser || this.state.showOptinsForm) {
      return
    }

    try {
      const json = await refresh(currentUser)

      if (json && json.token) {
        this.props.setUser({ ...currentUser, token: json.token })

        if (Cookie.get(Cookies.TOKEN)) {
          Cookie.set(Cookies.TOKEN, JSON.stringify(json.token), { expires: 7 })
        }
      }
    } catch (error) {
      // Silent: a background reissue that fails must not disturb the user.
    }
  }

  /**
   * Initial load (/user/me) failing on a transient error (network/5xx, not a 401): leave the
   * loading state so the app does not stay stuck on the spinner, without deleting the cookie (a
   * later reload will retry the session). The user is sent back to the login page.
   */
  handleBootstrapFailure = () => {
    this.setState({ ready: true })
    this.navigateToLoginPage()
  }

  navigateToLoginPage = () => {
    if (!this.state.isWidgetRoute) {
      this.props.history.push(Routes.LOGIN)
    }
  }

  componentDidCatch (error, info) {
    this.setState({
      appCrashedInfo: { error: error, info: info }
    })
  }

  executeSetLanguage = language => {
    return new Promise((resolve) => {
      const { addTranslationForLanguage, setActiveLanguage, getActiveLanguage, getUser } = this.props
      setLanguage({
        language,
        addTranslationForLanguage,
        setActiveLanguage,
        getActiveLanguage,
        getUser: () => { return getUser },
        getPreviousLanguage: previousLanguage => {
          this.previouslySelectedLanguage = getActiveLanguage
        }
      }).then(() => {
        resolve()
      })
    })
  }

  /**
   * This is the same behaviour as we have in UserSwitch component. If we have a previous user id in cookie
   * We get the user data and then we save the user into global state. The switch user view won't be shown
   */
  autoLoadUser = () => {
    request(
      '/api/User/' + Cookie.get(Cookies.SWITCH_USER),
      HttpMethods.GET,
      null,
      this.props.getUser
    ).then(async json => {
      await this.getUserContextAndData(
        this.props.getUser.token,
        json.data.id,
        true,
        false,
        json.data.username
      )

      // set is ready to true
      this.setState({ ready: true })

      // auto navigate to internships
      this.navigateUserToInitialPageAfterLogin()
    })
  };

  /** This is called after login or after switching the user */
  navigateUserToInitialPageAfterLogin = () => {
    const {
      getUser: { roles }
    } = this.props
    /**
     * If the user is a nurse auto display the shifts manager after the internships are loaded
     */
    if (roles.length === 1) {
      if ([InstitutionRoles.NURSE, SchoolRoles.VALIDATOR].includes(roles[0])) {
        this.props.history.push(Routes.SHIFTS_MANAGER)
      } else if (roles[0] === OtherRoles.ADMIN) {
        this.props.history.push(Routes.CHANGELOG_MANAGEMENT)
      } else {
        this.props.history.push(Routes.INTERNSHIPS)
      }
    } else {
      this.props.history.push(Routes.INTERNSHIPS)
    }
  };

  /**
   * This is executed as a callback inside LoginView, after the user is signed in
   */
  handleAfterLogin = () => {
    const {
      getUser: { roles }
    } = this.props

    if (getContextFromRoles(roles) === Contexts.ADMIN) {
      // An administrator lands on the user selection page by default.
      this.props.history.push(Routes.SWITCH_USER)
      return
    }

    this.navigateUserToInitialPageAfterLogin()
  }

  refreshAppBannerWarning = async () => {
    // We use no-store option on fetch to be sure the app loads the last app-banner.json from the server and not from local disk cache.
    // Before using this options the file would not be updated with the last version and a big part of the users
    // would not get the new banner/maintenance mode.
    const response = await window.fetch(
      getCurrentUrl() + '/config/app-banner.json',
      { cache: 'no-store' }
    )

    let appBanner = null
    try {
      appBanner = await response.json()
    } catch (e) {
      console.error('Error parsing App banner json.')
      return
    }

    if (JSON.stringify(this.props.getAppBanner) === JSON.stringify(appBanner)) { return }

    if (typeof appBanner.message === 'undefined') {
      console.error('App banner "message" not found. Check config file.')
      return
    }
    if (typeof appBanner.type === 'undefined') {
      console.error('App banner "type" not found. Check config file.')
      return
    }
    if (typeof appBanner.maintenance === 'undefined') {
      console.error('App banner "maintenance" not found. Check config file.')
      return
    }
    if (
      appBanner.type !== 'info' &&
      appBanner.type !== 'warning' &&
      appBanner.type !== 'error'
    ) {
      console.error(
        'Unknown app banner type. Only "info", "warning", "error" are permitted.'
      )
      return
    }

    this.props.setAppBanner(appBanner)
  };

  logout = async () => {
    // clear the changeloglist
    this.props.resetChangelog()
    // clear evaluation questions
    this.props.clearEvaluationQuestions()
    // reset the tags
    this.props.resetTags()
    const allDataReady =
      Object.keys(this.props.getDataReady).filter(
        d => !this.props.getDataReady[d]
      ).length === 0
    await Cookie.remove(Cookies.SWITCH_USER)

    if (typeof this.props.getUser.switchUser !== 'undefined') {
      // A ROLE_ADMIN logging out while impersonating a profile must NOT be logged out entirely:
      // leave the switch-user mode and reload the admin's own context, which sends them back to
      // the user selection view.
      // The page is not reloaded here (the token is not persisted in a cookie through the SSO, so
      // a reload would really log them out).
      await this.setState({ ready: false })
      this.getUserContextAndData(this.props.getUser.token)
      return
    }

    await Cookie.remove(Cookies.TOKEN)

    // Preventing any further data storing in the app if the user disconnected since last requests.
    // This should be replaced by a request manager, or something else.
    if (!allDataReady) window.location.reload()

    await this.setState({
      ready: true
    })

    const accountId = JSON.parse(window.localStorage.getItem(StorageKeys.ACCOUNT_ID))
    window.localStorage.removeItem(StorageKeys.ACCOUNT_ID)

    if (accountId) {
      const logoutCallbackUrl = encodeURIComponent(`${window.location.protocol}//${window.location.host}/login`)
      window.location.href = `${appConfig.opalAuthServeurWebapp}?account_id=${accountId}&logout=true&logout_callback=${logoutCallbackUrl}`
    } else {
      this.navigateToLoginPage()
    }

    this.props.setUser(null)
  };

  /**
   * Switches to another profile of the account: the backend issues a new token bound to the target
   * profile; it gets persisted, then the whole user context is reloaded, just like after a login.
   * Under admin impersonation the token stays the admin's one: switching profile simply means
   * impersonating the other profile (?_switch_user).
   */
  handleSwitchProfile = async profileId => {
    this.setState({ ready: false })

    // Purge the data tied to the current profile before loading the ones of the new context.
    this.props.resetChangelog()
    this.props.clearEvaluationQuestions()
    this.props.resetTags()

    const currentUser = this.props.getUser

    try {
      if (currentUser.switchUser) {
        const profile = (currentUser.profiles ?? []).find(p => p.id === profileId)

        Cookie.set(Cookies.SWITCH_USER, JSON.stringify(profileId), { expires: 7 })
        await this.getUserContextAndData(
          currentUser.token,
          profileId,
          false,
          false,
          profile?.username ?? currentUser.switchUserLabel
        )
      } else {
        const json = await switchProfile(currentUser, profileId)

        // Only persist the token again when the user had checked "remember me" (a cookie already
        // exists): otherwise a mere profile switch would turn an ephemeral session into one kept for
        // 7 days on the machine. The in-memory token is updated by getUserContextAndData.
        if (Cookie.get(Cookies.TOKEN)) {
          Cookie.set(Cookies.TOKEN, JSON.stringify(json.token), { expires: 7 })
        }

        await this.getUserContextAndData(json.token)
      }

      this.navigateUserToInitialPageAfterLogin()
    } catch (error) {
      // The error is already notified by the global handler; just hand control back to the app.
      this.setState({ ready: true })
    }
  }

  getUserContextAndData = (
    token,
    switchUserId,
    preventSettingReadyToTrue = false,
    preventLoadingSuperAdmin = false,
    switchUserLabel
  ) => {
    return new Promise((resolve, reject) => {
      request('/user/me', HttpMethods.GET, null, {
        token: token,
        switchUser:
          typeof switchUserId === 'undefined' ? null : switchUserId
      })
        .then(async json => {
          if (typeof json.code !== 'undefined' && json.code === HttpStatuses.UNAUTHORIZED) {
            // Session yummy cookie has expired :(
            this.logout()
            return
          }
          if (json === 'set_optins' || json === 'set_optins_user') {
            // The account has not accepted the terms and conditions yet: keep the user on the
            // acceptance screen (OptinsForm) until it is done.
            this.props.setUser({ token: token })
            this.setState({
              ready: true,
              showOptinsForm: true,
              optinsForUser: json === 'set_optins_user'
            })
            return
          }

          // /user/me now returns { data, account, profiles }: normalise it into a
          // backward-compatible user object (identity fields pulled from the account).
          const user = normalizeCurrentUser(json)

          // check what's the user's language and use it
          if (user.language && user.language !== this.props.getActiveLanguage) {
            this.executeSetLanguage(user.language)
          }

          const context = getContextFromRoles(user.roles)
          user.context = context
          user.token = token
          if (typeof switchUserId !== 'undefined') {
            // A specific profile is impersonated through its numeric id (?_switch_user=<id>);
            // the label (username) is only used to render the banner.
            user.switchUser = switchUserId
            user.switchUserLabel = switchUserLabel
          }

          await this.props.setUser(user)
          Analytics.setUserDetails(user, this.props.getActiveLanguage)

          if (this.optinsDisplayed) {
            this.setState({ showOptinsForm: false })
          }

          if (!preventSettingReadyToTrue) {
            this.setState({
              ready: true
            })
          }

          if (context === Contexts.ADMIN && !preventSettingReadyToTrue) {
            this.props.history.push(Routes.SWITCH_USER)
            return
          }

          switch (user.context) {
            case Contexts.STUDENT:
              this.loadStudentData()
              break
            case Contexts.SCHOOL:
              this.loadSchoolData()
              break
            case Contexts.INSTITUTION:
              this.loadHospitalData()
              break
            case Contexts.ADMIN:
              if (!preventLoadingSuperAdmin) {
                this.loadSuperAdminData()
              }
              break
          }

          resolve()
        })
        .catch(err => {
          // Transient error (network, 5xx) — NOT an auth invalidation (the 401 is handled above
          // through logout()). The "remember me" cookie is NOT deleted: the token may still be
          // valid and a later reload must be able to retry. The caller takes it from here.
          reject(err)
        })
    })
  };

  loadSchoolData = async () => {
    this.props.setDataReady({
      schools: false,
      institutions: true,
      students: false,
      internships: true // Internships fetching is not done here for schools, as it is loaded later in the timeline when all other data are ready.
    })

    await this.updateReducer(
      `/schools/0/${appConfig.apiPageSize}`,
      'schools',
      this.props.setSchools,
      schools => {
        if (schools.length) {
          let school = schools[0]
          school = filterSectionsAndSetRootSectionOfSchool(
            school,
            this.props.t('Without section')
          )
          let sections = school.sections

          // User coordinated sections from /user are missing data, we replace them by the ones from /api
          if (this.props.getUser.roles.indexOf(SchoolRoles.COORDINATOR) > -1) {
            const mySections = this.props.getUser.coordinatedSections
            sections = sections.filter(s => {
              for (let i = 0; i < mySections.length; i++) {
                if (s.id === mySections[i].id) {
                  return true
                }
              }
              return false
            })
          }

          school.sections = sections
          return [school]
        }
        return []
      }
    )

    const user = this.props.getUser

    if (this.props.getSchools.length > 0) {
      const schoolId = this.props.getSchools[0].id

      this.props.fetchSchoolSectionsBySchool(schoolId, user)
      this.props.fetchInstitutionsWithActivatedPauseQuotaOption(schoolId, user)
      fetchSchoolOptions(user, { id: schoolId }).then(json => {
        if (json && json.data) {
          this.setState({ schoolOptions: json.data })
        }
      })
    }

    this.props.fetchCanSchoolEditSchedulesByInstitution(user)
    this.props.fetchInstitutionConstraints(user)
    this.props.fetchTags()

    this.updateReducer(
      `/students/0/${appConfig.apiPageSize}`,
      'students',
      this.props.setStudents,
      students => {
        return students
      }
    )
  };

  loadHospitalData = async () => {
    const currentUser = this.props.getUser

    this.props.setDataReady({
      schools: currentUser.roles.includes(InstitutionRoles.INSTITUTION_GROUP_ADMIN),
      institutions: true,
      students: true,
      internships: true // Internship fetching is done here for hospitals, as it is loaded later in the timeline when all other data are ready.
    })

    if (!currentUser.roles.includes(InstitutionRoles.INSTITUTION_GROUP_ADMIN)) {
      await this.updateReducer(
        `/schools/0/${appConfig.apiPageSize}`,
        'schools',
        this.props.setSchools
      )

      const institution = currentUser.institutions[0]

      this.props.fetchTags()

      if (!currentUser.roles.includes(SchoolRoles.VALIDATOR)) {
        this.props.fetchInstitutionQuotas(institution.id, currentUser)
        this.props.setSelectedInstitution(institution)
      }
    }
  };

  loadSuperAdminData = () => {
    this.props.setDataReady({
      schools: false,
      institutions: false,
      students: true,
      internships: true // Internship fetching is done here for hospitals, as it is loaded later in the timeline when all other data are ready.
    })

    this.updateReducer(
      `/schools/0/${appConfig.apiPageSize}`,
      'schools',
      this.props.setSchools
    )
  }

  loadStudentData = () => {
    this.props.setDataReady({
      schools: true, // School fetching is not done for students, as current school is never displayed for a student.
      institutions: true, // Institution fetching not done for student, as it is loaded later in the internship display when all other data are ready.
      students: true // Students fetching is used for student, as the information is already loaded with /user/me at connection.
    })
  };

  updateReducer = async (
    requestUrl,
    getter,
    setter,
    callbackBeforeStoringData,
    callbackAfterStoringData
  ) => {
    let json = await request(requestUrl, HttpMethods.GET, null, this.props.getUser)
    let data = json.data

    while (json.nextPage) {
      json = await request(json.nextPage, HttpMethods.GET, null, this.props.getUser)
      data = data.concat(json.data)
    }

    if (typeof callbackBeforeStoringData !== 'undefined') {
      data = await callbackBeforeStoringData(data)
    }

    // Preventing any further data storing in the app if the user disconnected since last requests.
    // This should be replaced by a request manager, or something else.
    if (this.props.getUser !== null) {
      setter(data)

      const dataReady = objectDeepCopy(this.props.getDataReady)
      dataReady[getter] = true
      this.props.setDataReady(dataReady)
      if (typeof callbackAfterStoringData === 'function') {
        callbackAfterStoringData()
      }
    }
  };

  render () {
    if ((!this.state.ready || !this.state.languageSet) && !this.state.isWidgetRoute) {
      return <AppLoading />
    }

    const inMaintenanceMode =
      this.props.getAppBanner !== null &&
      typeof this.props.getAppBanner.maintenance &&
      this.props.getAppBanner.maintenance

    let context = null
    if (this.props.getUser.roles) {
      context = getContextFromRoles(this.props.getUser.roles)
    }

    return (
      <Theme variables={this.props.getTheme}>
        <AuthProvider>
          <GlobalProvider>
            <GlobalFiltersProvider>
              <AppCrash log={this.state.appCrashedInfo} onBack={() => this.setState({ appCrashedInfo: null })} />
              <ApiErrorCatcher />
              {inMaintenanceMode && (
                <div style={{ height: '100vh', padding: '10vw' }}>
                  <AppBanner />
                </div>
              )}
              {!inMaintenanceMode && this.state.showOptinsForm &&
                <AnonymousFormWrapper noBgImg>
                  <OptinsForm
                    onOptinsAccepted={(token) => {
                      // The backend may return a refreshed token; otherwise keep the current one.
                      const nextToken = token ?? this.props.getUser.token

                      // Only persist when a cookie already exists ("remember me" choice): accepting
                      // the optins must not turn an ephemeral session into one kept for 7 days.
                      if (Cookie.get(Cookies.TOKEN)) {
                        Cookie.set(Cookies.TOKEN, JSON.stringify(nextToken), { expires: 7 })
                      }
                      this.getUserContextAndData(nextToken).then(() => {
                        this.navigateUserToInitialPageAfterLogin()
                      })
                    }}
                    userIsNoStudent={this.state.optinsForUser}
                  />
                </AnonymousFormWrapper>}
              {!inMaintenanceMode && this.state.isWidgetRoute && (
                <Switch>
                  <Route path={Routes.SHIFTS_WIDGET}>
                    <ShiftsWidget />
                  </Route>
                </Switch>)}
              {!inMaintenanceMode && !this.state.isWidgetRoute && (
                <Switch>
                  <Route path={Routes.LOGIN}>
                    {!this.optinsDisplayed &&
                      <LoginView
                        onGetUserContextAndData={this.getUserContextAndData} //eslint-disable-line
                        onAfterLogin={this.handleAfterLogin}
                      />}
                  </Route>
                  <Route path={Routes.PASSWORD_RESET_REQUEST}>
                    {!this.optinsDisplayed &&
                      <AnonymousFormWrapper topBanner={<NonProdEnvironmentBanner />}>
                        <PasswordResetAskForm
                          onBack={() => {
                            this.navigateToLoginPage()
                          }}
                        />
                      </AnonymousFormWrapper>}
                  </Route>
                  <Route path={Routes.PASSWORD_RESET}>
                    {!this.optinsDisplayed &&
                      <AnonymousFormWrapper noBgImg>
                        <PasswordResetForm
                          onBack={() => {
                            this.navigateToLoginPage()
                          }}
                        />
                      </AnonymousFormWrapper>}
                  </Route>
                  <Route path={Routes.AUTH_CALLBACK}>
                    {!this.optinsDisplayed &&
                      <AuthCallback
                        onGetUserContextAndData={this.getUserContextAndData} //eslint-disable-line
                        onAfterLogin={this.handleAfterLogin}
                      />}
                  </Route>
                  <Route path={Routes.CONFIRM_ADDITIONAL_EMAIL}>
                    {!this.optinsDisplayed &&
                      <AnonymousFormWrapper noBgImg>
                        <ConfirmAdditionalEmail
                          onBack={() => {
                            this.navigateToLoginPage()
                          }}
                        />
                      </AnonymousFormWrapper>}
                  </Route>
                  <Route path={Routes.SWITCH_USER}>
                    {!this.optinsDisplayed &&
                      <AnonymousFormWrapper noBgImg>
                        <UserSwitch
                        onLogout={this.logout} //eslint-disable-line
                          switchUser={user => {
                            this.getUserContextAndData(
                              this.props.getUser.token,
                              user.id,
                              true,
                              false,
                              user.username
                            ).then(this.navigateUserToInitialPageAfterLogin)
                          }}
                        />
                      </AnonymousFormWrapper>}
                  </Route>
                  <Route path={Routes.SECTION_EVALUATION_BY_STUDENT}>
                    <AnonymousFormWrapper isWide noBgImg>
                      <Suspense fallback={<div style={{ display: 'flex', flexDirection: 'column', minHeight: '300px', position: 'relative' }} />}>
                        <SectionEvaluationByStudent sectionEvaluationByStudent />
                      </Suspense>
                    </AnonymousFormWrapper>
                  </Route>
                  <Route path='/*'>
                    {!this.optinsDisplayed && (
                      <SchoolProvider>
                        <AppManager
                        onLogout={this.logout} //eslint-disable-line
                          onSwitchProfile={this.handleSwitchProfile}
                          schoolOptions={this.state.schoolOptions}
                        />
                      </SchoolProvider>
                    )}
                  </Route>
                </Switch>
              )}
              {context && ![Contexts.NONE, Contexts.ADMIN].includes(context) && <ChangeLogNotification />}
              <ChatManager />
            </GlobalFiltersProvider>
          </GlobalProvider>
        </AuthProvider>
      </Theme>
    )
  }
}

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(App)
